import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, normalizeClass as _normalizeClass, withMemo as _withMemo, Teleport as _Teleport } from "vue"

const _hoisted_1 = { class: "flex h-full max-h-full flex-col" }
const _hoisted_2 = {
  key: 0,
  class: "flex h-full flex-col gap-2 overflow-y-scroll p-2.5 pt-0"
}
const _hoisted_3 = /*#__PURE__*/_createElementVNode("span", { class: "flex-grow" }, null, -1)
const _hoisted_4 = /*#__PURE__*/_createElementVNode("span", { class: "flex-grow" }, null, -1)

import { Ref, ref } from 'vue';
import { Router, useRouter } from 'vue-router';
import { RecycleScroller } from 'vue-virtual-scroller';
import { BlackDesertItem, BlackDesertItemHot } from '@blackdesertmarket/interfaces';
import { isValidBlackDesertItemHot } from '@blackdesertmarket/objects';
import { ListFilterData } from '@/interfaces/list-filter';
import { ItemSize } from '@/enums/item-size';
import { LoaderSize } from '@/enums/loader';
import { UseFluctuationTypeReturn, useFluctuationType } from '@/composables/item-hot/use-fluctuation-type';
import { UseItemHotReturn, useItemHot } from '@/composables/item-hot/use-item-hot';
import { UseItemHotFetchReturn, useItemHotFetch } from '@/composables/item-hot/use-item-hot-fetch';
import { UseItemReturn, useItem } from '@/composables/item/use-item';
import { UseListFilterReturn, useListFilter } from '@/composables/list-filter/use-list-filter';
import { UseDocumentSizeReturn, useDocumentSize } from '@/composables/use-document-size';
import { TranslateKey, useInject } from '@/composables/use-inject';
import { UseTailwindConfigReturn, useTailwindConfig } from '@/composables/use-tailwind-config';
import { UseVirtualScrollReturn, useVirtualScroll } from '@/composables/use-virtual-scroll';
import AppIcon from '@/components/Base/AppIcon.vue';
import AppLoader from '@/components/Base/AppLoader/AppLoader.vue';
import ItemDetailsModal from '@/components/ItemDetails/ItemDetailsModal.vue';
import ListFilter from '@/components/ListFilter/ListFilter.vue';
import ListItem from '@/components/ListItem/ListItem.vue';
import ListItemProperty from '@/components/ListItem/ListItemProperty.vue';
import ListItemSeparator from '@/components/ListItem/ListItemSeparator.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'ItemHotList',
  setup(__props) {

const translate = useInject(TranslateKey);
const router: Router = useRouter();
const tailwindConfig: UseTailwindConfigReturn = useTailwindConfig();
const documentSize: UseDocumentSizeReturn = useDocumentSize();
const itemHotFetch: UseItemHotFetchReturn = useItemHotFetch();

const loaded: Ref<boolean> = ref(false);
const search: Ref<boolean> = ref(false);
const itemList: Ref<BlackDesertItem[] | BlackDesertItemHot[]> = ref([]);
const activeItem: Ref<BlackDesertItemHot | null> = ref(null);

const getBasePrice = (item: BlackDesertItem | BlackDesertItemHot): string => {
  const itemComposable: UseItemReturn = useItem(item);
  return itemComposable.getBasePrice();
};

const getFluctuationPrice = (item: BlackDesertItem | BlackDesertItemHot): string => {
  if (!isValidBlackDesertItemHot(item)) {
    return '';
  }

  const itemHotComposable: UseItemHotReturn = useItemHot(item);
  return itemHotComposable.getFluctuationPrice();
};

const getFluctuationTypeClass = (item: BlackDesertItem | BlackDesertItemHot): string => {
  if (!isValidBlackDesertItemHot(item)) {
    return '';
  }

  const fluctuationType: UseFluctuationTypeReturn = useFluctuationType(item.fluctuationType);
  return fluctuationType.getBackgroundClass();
};

const getFluctuationTypeIcon = (item: BlackDesertItem | BlackDesertItemHot): string => {
  if (!isValidBlackDesertItemHot(item)) {
    return '';
  }

  const fluctuationType: UseFluctuationTypeReturn = useFluctuationType(item.fluctuationType);
  return fluctuationType.getIcon();
};

const prepareHotItemList = (data: BlackDesertItemHot[]) => {
  const virtualScroll: UseVirtualScrollReturn = useVirtualScroll();
  const gap: number = documentSize.remToRaw(tailwindConfig.getValue('gap', '2'));

  itemList.value = virtualScroll.prepareData<BlackDesertItem>(data, ItemSize.HEIGHT, gap);
};

const refetchHotItemList = (): Promise<void> => {
  return itemHotFetch.fetch().then((data: BlackDesertItemHot[]): void => {
    prepareHotItemList(data);
    loaded.value = true;
  });
};

const handleListItemClick = (item: BlackDesertItem | BlackDesertItemHot): void => {
  if (isValidBlackDesertItemHot(item)) {
    activeItem.value = activeItem.value !== item ? item : null;
    return;
  }

  router.push({
    name: 'item',
    params: {
      id: item.id,
    },
  });
};

const handleListFilter = async (data: ListFilterData): Promise<void> => {
  const listFilter: UseListFilterReturn = useListFilter(data);

  await listFilter.processItemList({
    itemList: itemList,
    loaded: loaded,
    fallback(): Promise<void> {
      return refetchHotItemList().then((): void => {
        search.value = false;
      });
    },
    callback(): void {
      search.value = true;
    },
  });
};

if (!itemList.value.length) {
  refetchHotItemList();
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(ListFilter, { onFilter: handleListFilter }),
      (search.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(itemList.value, (item) => {
              return (_openBlock(), _createBlock(ListItem, {
                key: item.id,
                item: item,
                onEffect: ($event: any) => (handleListItemClick(item))
              }, null, 8, ["item", "onEffect"]))
            }), 128))
          ]))
        : (_openBlock(), _createBlock(_unref(RecycleScroller), {
            key: 1,
            class: "p-2.5 pt-0",
            items: itemList.value,
            "skip-hover": ""
          }, {
            default: _withCtx(({ item }) => [
              _withMemo([item.id], () => (_openBlock(), _createBlock(ListItem, {
                key: item.id,
                item: item,
                class: _normalizeClass(getFluctuationTypeClass(item)),
                onEffect: ($event: any) => (handleListItemClick(item))
              }, {
                append: _withCtx(() => [
                  _createVNode(ListItemSeparator),
                  _createVNode(ListItemProperty, null, {
                    label: _withCtx(() => [
                      _createElementVNode("span", null, _toDisplayString(_unref(translate)('item.basePrice')), 1),
                      _hoisted_3,
                      _createElementVNode("span", null, "(" + _toDisplayString(getFluctuationPrice(item)) + ")", 1)
                    ]),
                    value: _withCtx(() => [
                      _createVNode(AppIcon, {
                        src: getFluctuationTypeIcon(item),
                        class: "h-[20px] drop-shadow"
                      }, null, 8, ["src"]),
                      _hoisted_4,
                      _createElementVNode("span", null, _toDisplayString(getBasePrice(item)), 1)
                    ]),
                    _: 2
                  }, 1024),
                  _createVNode(ListItemSeparator),
                  _createVNode(ListItemProperty, {
                    label: _unref(translate)('item.count'),
                    value: item.count
                  }, null, 8, ["label", "value"])
                ]),
                _: 2
              }, 1032, ["item", "class", "onEffect"])), _cache, 0),
              (activeItem.value === item)
                ? (_openBlock(), _createBlock(_Teleport, {
                    key: 0,
                    to: "#modal"
                  }, [
                    (_unref(isValidBlackDesertItemHot)(item))
                      ? (_openBlock(), _createBlock(ItemDetailsModal, {
                          key: 0,
                          id: item.id,
                          enhancement: item.enhancement,
                          onClose: ($event: any) => (handleListItemClick(item))
                        }, null, 8, ["id", "enhancement", "onClose"]))
                      : _createCommentVNode("", true)
                  ]))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["items"]))
    ]),
    (!loaded.value)
      ? (_openBlock(), _createBlock(AppLoader, {
          key: 0,
          size: _unref(LoaderSize).LARGE,
          overlay: ""
        }, null, 8, ["size"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})